import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";
import { BACKEND_URL } from "../utils/constants";

const AuthContext = createContext(null);

const STORAGE_KEY = "chacal-auth";

function loadStoredAuth() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed?.token && parsed?.user) return parsed;
  } catch {
    // ignore
  }
  return null;
}

export function AuthProvider({ children }) {
  const backendUrl = BACKEND_URL;

  const [auth, setAuth] = useState(loadStoredAuth);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const user = auth?.user || null;
  const token = auth?.token || null;
  const isAuthenticated = Boolean(token);

  useEffect(() => {
    if (auth) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(auth));
    } else {
      localStorage.removeItem(STORAGE_KEY);
    }
  }, [auth]);

  // Validate token on mount
  useEffect(() => {
    if (!token) return;
    axios
      .get(`${backendUrl}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 10000,
      })
      .then((res) => {
        if (res.data?.user) {
          setAuth((prev) => ({
            ...prev,
            user: { ...prev.user, ...res.data.user },
          }));
        }
      })
      .catch(() => {
        setAuth(null);
      });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const login = useCallback(
    async (username, password) => {
      setLoading(true);
      setError("");
      try {
        const res = await axios.post(
          `${backendUrl}/auth/login`,
          { username, password },
          { timeout: 10000 },
        );
        if (res.data?.token && res.data?.user) {
          setAuth({ token: res.data.token, user: res.data.user });
          return true;
        }
        setError("Unexpected response from server");
        return false;
      } catch (err) {
        const msg =
          err.response?.data?.message || "Login failed. Check your connection.";
        setError(msg);
        return false;
      } finally {
        setLoading(false);
      }
    },
    [backendUrl],
  );

  const logout = useCallback(() => {
    setAuth(null);
    setError("");
  }, []);

  const hasRole = useCallback(
    (...roles) => {
      if (!user) return false;
      return roles.includes(user.role);
    },
    [user],
  );

  return (
    <AuthContext.Provider
      value={{ user, token, isAuthenticated, loading, error, login, logout, hasRole }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
